import type { IcAfetado } from '@/features/changes/tipos';
import type { IcDaRelease } from '@/features/releases/tipos';

/**
 * Os itens de configuração que a versão de entrega pode declarar como movidos.
 *
 * Não há rota que liste o CMDB. O que existe, e faz sentido, é o que as
 * mudanças agrupadas **já declararam encostar**: se a versão moveu um servidor,
 * alguma das mudanças dentro dela mexeu nesse servidor. Montar a lista assim
 * mantém as duas declarações coerentes de graça.
 *
 * O que a própria versão já declarou entra junto, mesmo que a mudança que o
 * trouxe tenha sido retirada do conjunto depois: escondê-lo faria salvar apagar
 * o registro de algo que subiu.
 */

export interface CandidatoAIcDaRelease {
  readonly id: string;
  readonly name: string;
  readonly ciType: string;
}

const SEM_NOME = 'Item de configuração fora do catálogo';

export function icsDeclaradosPelasMudancas(
  porMudanca: ReadonlyArray<readonly IcAfetado[] | undefined>,
  jaNaRelease: readonly IcDaRelease[],
): CandidatoAIcDaRelease[] {
  const porId = new Map<string, CandidatoAIcDaRelease>();
  for (const lista of porMudanca) {
    for (const linha of lista ?? []) {
      if (porId.has(linha.ciId)) continue;
      porId.set(linha.ciId, {
        id: linha.ciId,
        name: linha.ci?.name ?? SEM_NOME,
        ciType: linha.ci?.ciType ?? '—',
      });
    }
  }
  for (const linha of jaNaRelease) {
    porId.set(linha.ciId, { id: linha.ciId, name: linha.ci.name, ciType: linha.ci.ciType });
  }
  return [...porId.values()];
}

/**
 * A assinatura da lista gravada — faz as vezes de versão.
 *
 * `POST /releases/:id/cis` substitui tudo e não incrementa `Release.version`,
 * então não há guarda de concorrência neste caminho. Comparar o conteúdo
 * detecta a mesma corrida: se o que veio do servidor mudou depois de o
 * formulário ter sido semeado, alguém mexeu na lista.
 */
export function assinaturaDeIcsDaRelease(linhas: readonly IcDaRelease[]): string {
  return linhas
    .map((linha) => `${linha.ciId}:${linha.versionBefore ?? ''}:${linha.versionAfter ?? ''}`)
    .sort()
    .join('|');
}
